import React, { useState, useEffect } from 'react';
import QRCode from 'qrcode';

const QRCodeRenderer = ({ 
  data, 
  size = 256, 
  margin = 2, 
  errorCorrectionLevel = 'M',
  className = '',
  onError,
  onLoad
}) => {
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!data) {
      setError('No data provided');
      setIsLoading(false);
      return;
    }

    const generateQRCode = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const dataUrl = await QRCode.toDataURL(data, {
          width: size,
          height: size,
          margin,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          },
          errorCorrectionLevel,
          type: 'image/png',
          quality: 0.92,
          rendererOpts: {
            quality: 0.92
          }
        });

        setQrCodeDataUrl(dataUrl);
        onLoad?.(dataUrl);
      } catch (err) {
        console.error('QR Code generation error:', err);
        const errorMessage = 'Failed to generate QR code';
        setError(errorMessage);
        onError?.(err, errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    generateQRCode();
  }, [data, size, margin, errorCorrectionLevel, onLoad, onError]);

  if (isLoading) {
    return (
      <div 
        className={`flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-lg ${className}`}
        style={{ width: size, height: size }}
      >
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div 
        className={`flex items-center justify-center bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 ${className}`}
        style={{ width: size, height: size }}
      >
        <div className="text-center">
          <svg className="w-8 h-8 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <p className="text-sm">Failed to generate QR code</p>
        </div>
      </div>
    );
  }

  if (!qrCodeDataUrl) {
    return null;
  }

  return (
    <div className={className}>
      <img
        src={qrCodeDataUrl}
        alt="QR Code"
        width={size}
        height={size}
        className="rounded-lg"
        style={{ maxWidth: '100%', height: 'auto' }}
      />
    </div>
  );
};

export default QRCodeRenderer;
